import * as THREE from 'three';
import { MorpheusMap } from '../map/MorpheusMap';
import { WeaponSystem, WeaponType } from '../weapons/WeaponSystem';
import { fpsAudio } from '../audio/FPSAudio';

export interface BotConfig {
  name: string;
  color: number;
  visorColor: number;
  skill: number; // 0.5 - 1.0
  favoriteWeapon: WeaponType;
}

export const BOT_CONFIGS: BotConfig[] = [
  { name: 'Xan Kriegor', color: 0xa0b4c8, visorColor: 0x00ffff, skill: 0.95, favoriteWeapon: 'shock' },
  { name: 'Loque', color: 0xd97706, visorColor: 0xff3300, skill: 0.98, favoriteWeapon: 'sniper' },
  { name: 'Malcolm', color: 0x22c55e, visorColor: 0x39ff14, skill: 0.88, favoriteWeapon: 'flak' },
  { name: 'Brock', color: 0xef4444, visorColor: 0xffaa00, skill: 0.82, favoriteWeapon: 'rocket' },
  { name: 'Lauren', color: 0xa855f7, visorColor: 0xff00ff, skill: 0.85, favoriteWeapon: 'pulse' },
  { name: 'Necroth', color: 0x38bdf8, visorColor: 0x00ffff, skill: 0.80, favoriteWeapon: 'shock' },
];

export class BotAI {
  public id: string;
  public name: string;
  public mesh: THREE.Group;
  public velocity: THREE.Vector3 = new THREE.Vector3();
  public health: number = 100;
  public armor: number = 50;
  public score: number = 0;
  public deaths: number = 0;
  public isAlive: boolean = true;
  public respawnTimer: number = 0;
  public currentWeapon: WeaponType = 'enforcer';
  public skill: number;
  public favoriteWeapon: WeaponType;

  private currentWaypointIndex: number = 0;
  private state: 'roam' | 'hunt' | 'flee' = 'roam';
  private fireTimer: number = 0;
  private changeTargetTimer: number = 0;
  private currentTarget: { id: string; getPosition: () => THREE.Vector3; isAlive: boolean } | null = null;
  private jumpCooldown: number = 0;
  private onGround: boolean = false;

  // Visual model components
  private bodyMesh: THREE.Mesh;
  private headMesh: THREE.Mesh;
  private weaponMesh: THREE.Mesh;

  constructor(id: string, config: BotConfig, initialPos: THREE.Vector3) {
    this.id = id;
    this.name = config.name;
    this.skill = config.skill;
    this.favoriteWeapon = config.favoriteWeapon;
    this.currentWeapon = config.favoriteWeapon;

    this.mesh = new THREE.Group();
    this.mesh.position.copy(initialPos);

    // Build Sci-Fi Bot Character Model with distinct silhouettes and materials
    const armorMat = new THREE.MeshStandardMaterial({
      color: config.color,
      metalness: 0.6,
      roughness: 0.35,
    });
    const jointMat = new THREE.MeshStandardMaterial({
      color: 0x334155, // Clean slate undersuit
      metalness: 0.5,
      roughness: 0.5,
    });
    const plateMat = new THREE.MeshStandardMaterial({
      color: 0xe2e8f0, // Reinforced silver chest plating
      metalness: 0.7,
      roughness: 0.25,
    });
    const visorMat = new THREE.MeshBasicMaterial({
      color: config.visorColor,
    });

    // Torso / Chest Armor
    const torsoGeo = new THREE.BoxGeometry(0.7, 0.8, 0.45);
    this.bodyMesh = new THREE.Mesh(torsoGeo, armorMat);
    this.bodyMesh.position.y = 1.1;
    this.mesh.add(this.bodyMesh);

    // Reinforced Chest Plate with Glowing Core
    const chestPlate = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.55, 0.1), plateMat);
    chestPlate.position.set(0, 0.05, 0.22);
    this.bodyMesh.add(chestPlate);

    const chestCore = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.2, 0.06), visorMat);
    chestCore.position.set(0, 0.08, 0.27);
    this.bodyMesh.add(chestCore);

    // Shoulders with glowing energy trims
    const shoulderGeo = new THREE.BoxGeometry(0.28, 0.3, 0.38);
    const leftShoulder = new THREE.Mesh(shoulderGeo, armorMat);
    leftShoulder.position.set(-0.48, 1.35, 0);
    const rightShoulder = new THREE.Mesh(shoulderGeo, armorMat);
    rightShoulder.position.set(0.48, 1.35, 0);

    const sTrimGeo = new THREE.BoxGeometry(0.06, 0.32, 0.4);
    const leftTrim = new THREE.Mesh(sTrimGeo, visorMat);
    leftTrim.position.set(-0.13, 0, 0);
    leftShoulder.add(leftTrim);
    const rightTrim = new THREE.Mesh(sTrimGeo, visorMat);
    rightTrim.position.set(0.13, 0, 0);
    rightShoulder.add(rightTrim);

    this.mesh.add(leftShoulder, rightShoulder);

    // Head / Helmet
    const headGeo = new THREE.BoxGeometry(0.38, 0.4, 0.4);
    this.headMesh = new THREE.Mesh(headGeo, armorMat);
    this.headMesh.position.y = 1.7;

    // Helmet Forehead Crest
    const crest = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.1, 0.3), plateMat);
    crest.position.set(0, 0.18, 0.05);
    this.headMesh.add(crest);

    // Glowing Visor
    const visorGeo = new THREE.BoxGeometry(0.3, 0.12, 0.1);
    const visor = new THREE.Mesh(visorGeo, visorMat);
    visor.position.set(0, 0.02, 0.18);
    this.headMesh.add(visor);
    this.mesh.add(this.headMesh);

    // Legs with knee plates
    const legGeo = new THREE.BoxGeometry(0.24, 0.8, 0.26);
    const leftLeg = new THREE.Mesh(legGeo, jointMat);
    leftLeg.position.set(-0.2, 0.4, 0);
    const rightLeg = new THREE.Mesh(legGeo, jointMat);
    rightLeg.position.set(0.2, 0.4, 0);

    const kneeGeo = new THREE.BoxGeometry(0.26, 0.2, 0.1);
    const leftKnee = new THREE.Mesh(kneeGeo, armorMat);
    leftKnee.position.set(0, 0.05, 0.12);
    leftLeg.add(leftKnee);
    const rightKnee = new THREE.Mesh(kneeGeo, armorMat);
    rightKnee.position.set(0, 0.05, 0.12);
    rightLeg.add(rightKnee);

    this.mesh.add(leftLeg, rightLeg);

    // Held Weapon in hand
    this.weaponMesh = new THREE.Mesh();
    this.updateWeaponMesh(this.currentWeapon);
    this.mesh.add(this.weaponMesh);
  }

  public updateWeaponMesh(weaponType: WeaponType) {
    if (this.weaponMesh) {
      this.mesh.remove(this.weaponMesh);
      // Clean up children
      while (this.weaponMesh.children.length > 0) {
        this.weaponMesh.remove(this.weaponMesh.children[0]);
      }
    }

    const gunGroup = new THREE.Group();
    gunGroup.position.set(0.42, 1.05, 0.35);

    switch (weaponType) {
      case 'enforcer': {
        const slide = new THREE.Mesh(
          new THREE.BoxGeometry(0.1, 0.12, 0.38),
          new THREE.MeshStandardMaterial({ color: 0x5b6b82, metalness: 0.8 })
        );
        gunGroup.add(slide);
        const sight = new THREE.Mesh(new THREE.BoxGeometry(0.02, 0.03, 0.04), new THREE.MeshBasicMaterial({ color: 0xffff00 }));
        sight.position.set(0, 0.075, 0.1);
        gunGroup.add(sight);
        break;
      }

      case 'shock': {
        const body = new THREE.Mesh(
          new THREE.BoxGeometry(0.1, 0.12, 0.65),
          new THREE.MeshStandardMaterial({ color: 0x3b1d60, metalness: 0.7 })
        );
        gunGroup.add(body);
        const core = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.16, 8), new THREE.MeshBasicMaterial({ color: 0x00fff2 }));
        core.rotation.x = Math.PI / 2;
        gunGroup.add(core);
        [-0.05, 0.05].forEach((x) => {
          const prong = new THREE.Mesh(
            new THREE.BoxGeometry(0.015, 0.02, 0.22),
            new THREE.MeshBasicMaterial({ color: 0x00fff2 })
          );
          prong.position.set(x, 0, 0.4);
          gunGroup.add(prong);
        });
        break;
      }

      case 'pulse': {
        const body = new THREE.Mesh(
          new THREE.BoxGeometry(0.14, 0.16, 0.48),
          new THREE.MeshStandardMaterial({ color: 0x334155, metalness: 0.7 })
        );
        gunGroup.add(body);
        const cell = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.18, 8), new THREE.MeshBasicMaterial({ color: 0x39ff14 }));
        cell.rotation.x = Math.PI / 2;
        cell.position.y = -0.06;
        gunGroup.add(cell);
        const b = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 0.25, 8), new THREE.MeshBasicMaterial({ color: 0x39ff14 }));
        b.rotation.x = Math.PI / 2;
        b.position.set(0, 0, 0.35);
        gunGroup.add(b);
        break;
      }

      case 'flak': {
        const body = new THREE.Mesh(
          new THREE.BoxGeometry(0.18, 0.18, 0.5),
          new THREE.MeshStandardMaterial({ color: 0xf59e0b, metalness: 0.6 })
        );
        gunGroup.add(body);
        const mouth = new THREE.Mesh(
          new THREE.BoxGeometry(0.22, 0.12, 0.12),
          new THREE.MeshStandardMaterial({ color: 0xd97706 })
        );
        mouth.position.set(0, 0.02, 0.28);
        gunGroup.add(mouth);
        const mouthSlot = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.05, 0.05), new THREE.MeshBasicMaterial({ color: 0x050505 }));
        mouthSlot.position.set(0, 0.02, 0.34);
        gunGroup.add(mouthSlot);
        break;
      }

      case 'rocket': {
        const body = new THREE.Mesh(
          new THREE.BoxGeometry(0.18, 0.18, 0.55),
          new THREE.MeshStandardMaterial({ color: 0x991b1b, metalness: 0.7 })
        );
        gunGroup.add(body);
        const drum = new THREE.Mesh(
          new THREE.CylinderGeometry(0.09, 0.09, 0.24, 12),
          new THREE.MeshStandardMaterial({ color: 0x4b5563, metalness: 0.8 })
        );
        drum.rotation.x = Math.PI / 2;
        drum.position.set(0, 0.02, 0.35);
        gunGroup.add(drum);
        break;
      }

      case 'sniper': {
        const body = new THREE.Mesh(
          new THREE.BoxGeometry(0.08, 0.12, 0.65),
          new THREE.MeshStandardMaterial({ color: 0x1e293b, metalness: 0.8 })
        );
        gunGroup.add(body);
        const barrel = new THREE.Mesh(
          new THREE.CylinderGeometry(0.02, 0.02, 0.5, 8),
          new THREE.MeshStandardMaterial({ color: 0x64748b, metalness: 0.9 })
        );
        barrel.rotation.x = Math.PI / 2;
        barrel.position.set(0, 0.02, 0.55);
        gunGroup.add(barrel);
        const scope = new THREE.Mesh(
          new THREE.CylinderGeometry(0.035, 0.035, 0.28, 10),
          new THREE.MeshStandardMaterial({ color: 0x0f172a })
        );
        scope.rotation.x = Math.PI / 2;
        scope.position.set(0, 0.11, 0.05);
        gunGroup.add(scope);
        break;
      }
    }

    this.weaponMesh = gunGroup as any;
    this.mesh.add(this.weaponMesh);
  }

  public takeDamage(amount: number, attackerId: string): { killed: boolean; wasHeadshot: boolean } {
    if (!this.isAlive) return { killed: false, wasHeadshot: false };

    // Armor absorbs 70% of damage
    if (this.armor > 0) {
      const absorbed = Math.min(this.armor, amount * 0.7);
      this.armor -= absorbed;
      amount -= absorbed;
    }

    this.health -= amount;

    if (this.health <= 0) {
      this.health = 0;
      this.isAlive = false;
      this.deaths++;
      this.respawnTimer = 3.5;
      this.mesh.visible = false;
      return { killed: true, wasHeadshot: false };
    }

    return { killed: false, wasHeadshot: false };
  }

  public respawn(spawnPos: THREE.Vector3) {
    this.mesh.position.copy(spawnPos);
    this.velocity.set(0, 0, 0);
    this.health = 100;
    this.armor = 50;
    this.isAlive = true;
    this.mesh.visible = true;
    this.state = 'roam';
    this.currentTarget = null;
    this.currentWeapon = this.favoriteWeapon;
    this.updateWeaponMesh(this.currentWeapon);
  }

  public update(
    deltaTime: number,
    map: MorpheusMap,
    weaponSystem: WeaponSystem,
    potentialTargets: { id: string; getPosition: () => THREE.Vector3; isAlive: boolean }[]
  ) {
    if (!this.isAlive) {
      this.respawnTimer -= deltaTime;
      if (this.respawnTimer <= 0) {
        // Pick random spawn
        const spawn = map.spawnPoints[Math.floor(Math.random() * map.spawnPoints.length)];
        this.respawn(spawn);
      }
      return;
    }

    // Death abyss check
    if (this.mesh.position.y < -25) {
      this.isAlive = false;
      this.deaths++;
      this.respawnTimer = 3.5;
      this.mesh.visible = false;
      return;
    }

    this.jumpCooldown = Math.max(0, this.jumpCooldown - deltaTime);
    this.fireTimer = Math.max(0, this.fireTimer - deltaTime);
    this.changeTargetTimer -= deltaTime;

    // Pick target periodically or if current target died
    if (this.changeTargetTimer <= 0 || !this.currentTarget || !this.currentTarget.isAlive) {
      this.changeTargetTimer = 2.0 + Math.random() * 2.0;
      this.selectTarget(potentialTargets);
    }

    // Check Jump Pads
    for (const pad of map.jumpPads) {
      const dist = new THREE.Vector2(this.mesh.position.x - pad.position.x, this.mesh.position.z - pad.position.z).length();
      if (dist < pad.radius && Math.abs(this.mesh.position.y - pad.position.y) < 2.0 && this.jumpCooldown <= 0) {
        // Boost into the air
        this.velocity.copy(pad.targetVelocity);
        this.jumpCooldown = 1.5;
        this.onGround = false;
        break;
      }
    }

    // AI Decision: Target Tracking & Combat
    let moveGoal = new THREE.Vector3();
    const myPos = this.mesh.position;

    if (this.currentTarget && this.currentTarget.isAlive) {
      const targetPos = this.currentTarget.getPosition();
      const distToTarget = myPos.distanceTo(targetPos);

      if (distToTarget < 50) {
        // In engagement range
        this.state = 'hunt';
        // Face target
        const lookDir = targetPos.clone().sub(myPos).setY(0).normalize();
        if (lookDir.lengthSq() > 0.001) {
          this.mesh.rotation.y = Math.atan2(lookDir.x, lookDir.z);
        }

        // Aim and fire
        if (this.fireTimer <= 0) {
          this.fireAtTarget(targetPos, weaponSystem);
        }

        // Tactical movement: strafe and maintain combat distance
        if (distToTarget > 20) {
          moveGoal.copy(targetPos);
        } else if (distToTarget < 8) {
          // Back up if too close
          moveGoal.copy(myPos).sub(lookDir.clone().multiplyScalar(10));
        } else {
          // Circle strafe
          const strafeDir = new THREE.Vector3(-lookDir.z, 0, lookDir.x);
          moveGoal.copy(myPos).add(strafeDir.multiplyScalar(Math.random() > 0.5 ? 8 : -8));
        }

        // Occasional tactical jump in low gravity
        if (this.onGround && Math.random() < 0.02) {
          this.velocity.y = 8.5;
          this.onGround = false;
        }
      } else {
        this.state = 'roam';
      }
    } else {
      this.state = 'roam';
    }

    // Roam waypoint navigation if not hunting or roaming
    if (this.state === 'roam') {
      const wp = map.waypoints[this.currentWaypointIndex];
      moveGoal.copy(wp);

      const horizDist = new THREE.Vector2(myPos.x - wp.x, myPos.z - wp.z).length();
      if (horizDist < 3.0) {
        this.currentWaypointIndex = Math.floor(Math.random() * map.waypoints.length);
      }

      const moveDir = wp.clone().sub(myPos).setY(0).normalize();
      if (moveDir.lengthSq() > 0.001) {
        this.mesh.rotation.y = Math.atan2(moveDir.x, moveDir.z);
      }
    }

    // Apply horizontal movement toward moveGoal
    const targetDir = moveGoal.clone().sub(myPos).setY(0).normalize();
    const speed = 12.0;

    if (this.onGround) {
      this.velocity.x = THREE.MathUtils.lerp(this.velocity.x, targetDir.x * speed, 0.15);
      this.velocity.z = THREE.MathUtils.lerp(this.velocity.z, targetDir.z * speed, 0.15);
    } else {
      // Floatier air control in low gravity
      this.velocity.x = THREE.MathUtils.lerp(this.velocity.x, targetDir.x * speed, 0.04);
      this.velocity.z = THREE.MathUtils.lerp(this.velocity.z, targetDir.z * speed, 0.04);
    }

    // Low Gravity (-10 m/s²)
    this.velocity.y -= 10.0 * deltaTime;

    // Tentative new position
    const nextPos = myPos.clone().addScaledVector(this.velocity, deltaTime);

    // Collision Detection against Map Colliders
    this.resolveMapCollisions(nextPos, map);

    // Check Pickups
    for (const pickup of map.pickups) {
      if (!pickup.active) continue;
      if (myPos.distanceTo(pickup.position) < 2.0) {
        pickup.active = false;
        pickup.mesh.visible = false;
        pickup.timer = pickup.respawnTime;

        if (pickup.type === 'health_pack') {
          this.health = Math.min(100, this.health + 25);
        } else if (pickup.type === 'health_vial') {
          this.health = Math.min(199, this.health + 5);
        } else if (pickup.type === 'armor') {
          this.armor = Math.min(100, this.armor + 50);
        } else if (pickup.type === 'shield_belt') {
          this.armor = Math.min(150, this.armor + 100);
        } else if (pickup.type.startsWith('weapon_')) {
          const wType = pickup.type.replace('weapon_', '') as WeaponType;
          this.currentWeapon = wType;
          this.updateWeaponMesh(wType);
        }
      }
    }
  }

  private resolveMapCollisions(nextPos: THREE.Vector3, map: MorpheusMap) {
    const botRadius = 0.5;
    const botHeight = 1.9;
    const stepHeight = 0.4;
    this.onGround = false;

    // Vertical sweep
    const botBoxY = new THREE.Box3(
      new THREE.Vector3(this.mesh.position.x - botRadius, nextPos.y, this.mesh.position.z - botRadius),
      new THREE.Vector3(this.mesh.position.x + botRadius, nextPos.y + botHeight, this.mesh.position.z + botRadius)
    );

    for (const col of map.colliders) {
      if (botBoxY.intersectsBox(col.box)) {
        if (this.velocity.y <= 0 && this.mesh.position.y >= col.box.max.y - 0.5) {
          // Landed on floor
          nextPos.y = col.box.max.y;
          this.velocity.y = 0;
          this.onGround = true;
        } else if (this.velocity.y > 0 && this.mesh.position.y + botHeight <= col.box.min.y + 0.4) {
          // Hit ceiling
          nextPos.y = col.box.min.y - botHeight;
          this.velocity.y = 0;
        }
      }
    }

    const bodyBottom = nextPos.y + stepHeight;
    const bodyTop = nextPos.y + botHeight;

    // Horizontal sweep
    const botBoxX = new THREE.Box3(
      new THREE.Vector3(nextPos.x - botRadius, bodyBottom, this.mesh.position.z - botRadius),
      new THREE.Vector3(nextPos.x + botRadius, bodyTop, this.mesh.position.z + botRadius)
    );
    for (const col of map.colliders) {
      if (col.type === 'floor' && col.box.max.y <= nextPos.y + stepHeight) continue;
      if (botBoxX.intersectsBox(col.box)) {
        nextPos.x = this.mesh.position.x;
        this.velocity.x = 0;
        break;
      }
    }

    const botBoxZ = new THREE.Box3(
      new THREE.Vector3(nextPos.x - botRadius, bodyBottom, nextPos.z - botRadius),
      new THREE.Vector3(nextPos.x + botRadius, bodyTop, nextPos.z + botRadius)
    );
    for (const col of map.colliders) {
      if (col.type === 'floor' && col.box.max.y <= nextPos.y + stepHeight) continue;
      if (botBoxZ.intersectsBox(col.box)) {
        nextPos.z = this.mesh.position.z;
        this.velocity.z = 0;
        break;
      }
    }

    this.mesh.position.copy(nextPos);
  }

  private selectTarget(potentialTargets: { id: string; getPosition: () => THREE.Vector3; isAlive: boolean }[]) {
    const viable = potentialTargets.filter((t) => t.id !== this.id && t.isAlive);
    if (viable.length === 0) {
      this.currentTarget = null;
      return;
    }

    // Pick closest target
    let closest = viable[0];
    let minDist = this.mesh.position.distanceTo(closest.getPosition());
    for (let i = 1; i < viable.length; i++) {
      const d = this.mesh.position.distanceTo(viable[i].getPosition());
      if (d < minDist) {
        minDist = d;
        closest = viable[i];
      }
    }
    this.currentTarget = closest;
  }

  private fireAtTarget(targetPos: THREE.Vector3, weaponSystem: WeaponSystem) {
    const origin = this.mesh.position.clone().add(new THREE.Vector3(0, 1.3, 0));
    const aimSpread = (1.0 - this.skill) * 1.8;
    const targetWithSpread = targetPos
      .clone()
      .add(
        new THREE.Vector3(
          (Math.random() - 0.5) * aimSpread,
          (Math.random() - 0.5) * aimSpread * 0.6,
          (Math.random() - 0.5) * aimSpread
        )
      );

    const dir = targetWithSpread.sub(origin).normalize();

    // Rates of fire per weapon
    const cooldownMap: Record<WeaponType, number> = {
      enforcer: 0.38,
      shock: 0.75,
      pulse: 0.12,
      flak: 0.85,
      rocket: 1.1,
      sniper: 1.25,
    };

    const isAlt = this.currentWeapon === 'shock' ? Math.random() < 0.3 : Math.random() < 0.2;
    weaponSystem.fire(this.currentWeapon, this.id, origin, dir, isAlt);
    this.fireTimer = cooldownMap[this.currentWeapon] || 0.5;
  }
}
